<script
  setup
  lang="ts"
  generic="TEntity extends Exclude<keyof GA4Entities, 'cartList' & 'gaItem'>"
>
import type { GA4Entities } from "nuxt-ga4"
import type { UseIntersectionObserverOptions } from "@vueuse/core"
import { viewItemListMethod } from "~/utils/analytics/viewItemList"

const props = withDefaults(
  defineProps<{
    observerOptions?: UseIntersectionObserverOptions
    reRender?: boolean // It allows you to re-render the execution of this event WHENEVER the "statefulEntityItem?.value" re-evalutes (from "GAItem")
    method?: string
  }>(),
  { observerOptions: { threshold: 0.9 }, method: "standard" }
)

const id = inject("id")
const statefulEntityItem: GA4Entities["gaItem"] | undefined =
  inject("statefulEntityItem")

const eventName = "view_item_list"
const eventKey = `event_${eventName}_${id}`
const event = "view_item_list"

const route = useRoute()
let gtmMethod = props.method
if (gtmMethod != viewItemListMethod.search) {
  if (route.matched.some(({ path }) => path.startsWith("/carrello"))) {
    gtmMethod = viewItemListMethod.cart
  } else if (route.matched.some(({ path }) => path.startsWith("/checkout"))) {
    gtmMethod = viewItemListMethod.checkout
  }
}
const containerRef = ref<HTMLElement>()
const gaEventRef = ref<any>()

const handleIntersectionObserver = () => {
  return useIntersectionObserver(
    containerRef,
    (entries, observer) => {
      for (let entry of entries) {
        if (entry.isIntersecting) {
          if (Object.keys(statefulEntityItem?.value ?? {}).length) {
            gaEventRef.value?.sendEvent({ clearEcommerce: false })
          }
          observer.disconnect()
        }
      }
    },
    props.observerOptions
  )
}

// MAIN EXECUTION
const { stop: stopMainExecution } = handleIntersectionObserver()
const previousReRenderExecution = ref<() => void>(() => null)

// RE-RENDER EXECUTION
if (props?.reRender) {
  watch(
    () => statefulEntityItem?.value,
    (newValue, oldValue) => {
      if (!isEqual(newValue, oldValue)) {
        // Whenever we re-render the GA execution, we must STOP these, to avoid repeated executions
        previousReRenderExecution.value() // Stop the PREVIOUS RE-RENDER
        stopMainExecution() // Stop the MAIN ONE

        const { stop: stopReRenderExecution } = handleIntersectionObserver()
        previousReRenderExecution.value = stopReRenderExecution
      }
    }
  )
}

const payload = computed(() => {
  return {
    event,
    method: gtmMethod,
    ecommerce: {
      items: statefulEntityItem?.value ?? []
    }
  }
})
</script>

<template>
  <div ref="containerRef" class="h-full w-full">
    <GAEvent
      event="custom_event"
      :eventKey="eventKey"
      :payload="payload"
      :lazy="true"
      ref="gaEventRef"
    >
      <slot />
    </GAEvent>
  </div>
</template>
